import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../../context/CartContext";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Products", href: "/products" },
  { label: "Contact", href: "/contact" },
];

const LOGO_SRC = "/assets/logo/tiwani-logo-header.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { totalItems, openCart } = useCart();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const mobileMenuActive = mobileOpen;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const scrollPageToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const solid = (scrolled || !isHome) && !mobileMenuActive;
  const bgClass = solid ? "bg-white/95 backdrop-blur-md shadow-sm py-4" : "bg-transparent py-6";
  const linkColor = solid ? "text-brand-charcoal hover:text-brand-red" : "text-white/90 hover:text-white";
  const iconColor = solid ? "text-brand-charcoal hover:text-brand-red" : "text-white hover:text-white/70";
  const logoClass = solid ? "" : "brightness-0 invert";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${bgClass}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center" onClick={scrollPageToTop}>
            <img src={LOGO_SRC} alt="Tiwani" className={`h-10 sm:h-11 w-auto object-contain transition-all duration-300 ${logoClass}`} loading="eager" fetchPriority="high" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map(({ label, href }) => {
              const active = location.pathname === href;
              return (
                <Link
                  key={label}
                  to={href}
                  onClick={href === "/" ? scrollPageToTop : undefined}
                  className={`relative font-body text-sm tracking-wide transition-colors duration-200 group ${linkColor} ${active ? "!text-brand-red" : ""}`}
                >
                  {label}
                  <span className={`absolute -bottom-1 left-0 h-px bg-brand-red transition-all duration-300 ${active ? "w-full" : "w-0 group-hover:w-full"}`} />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={openCart} aria-label="Open cart" className={`relative p-2 transition-colors duration-200 ${iconColor}`}>
              <ShoppingBag size={21} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-brand-red text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </button>
            <Link to="/contact" className="hidden lg:inline-flex items-center px-5 py-2.5 bg-brand-red text-white text-sm font-body font-medium tracking-wide rounded-sm hover:bg-red-700 transition-colors duration-200">
              Contact Us
            </Link>
            <button onClick={() => setMobileOpen((open) => !open)} aria-label="Toggle menu" className={`lg:hidden p-2 transition-colors ${solid ? "text-brand-dark" : "text-white"}`}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${mobileOpen ? "visible opacity-100" : "invisible opacity-0"}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div className={`absolute top-0 right-0 h-full w-72 border-l border-white/10 bg-brand-dark/95 backdrop-blur-xl shadow-2xl transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "translate-x-full"}`}>
          <div className="pt-10 px-8 h-full flex flex-col">
            <Link to="/" className="inline-flex items-center mb-10" onClick={scrollPageToTop}>
              <img src={LOGO_SRC} alt="Tiwani" className="h-10 w-auto object-contain brightness-0 invert" loading="eager" fetchPriority="high" />
            </Link>
            <p className="font-body text-[10px] uppercase tracking-[0.3em] text-white/45 mb-5">Navigation</p>
            <nav className="flex flex-col">
              {NAV_LINKS.map(({ label, href }) => (
                <Link
                  key={label}
                  to={href}
                  onClick={href === "/" ? scrollPageToTop : undefined}
                  className={`font-body text-lg py-4 border-b border-white/10 transition-colors ${
                    location.pathname === href ? "text-brand-red" : "text-white hover:text-brand-red"
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>
            <p className="mt-6 font-body text-sm text-white/60 leading-relaxed">
              Discover elegant fragrances, shop with confidence, and reach us anytime.
            </p>
            <Link to="/contact" className="mt-8 w-full flex items-center justify-center px-5 py-3.5 bg-brand-red text-white font-body font-medium text-sm rounded-sm hover:bg-red-700 transition-colors">
              Contact Us
            </Link>
            {totalItems > 0 && (
              <p className="mt-4 font-body text-xs text-white/40">
                You have {totalItems} item{totalItems === 1 ? "" : "s"} in your cart.
              </p>
            )}
            <div className="mt-auto pb-8" />
          </div>
        </div>
      </div>
    </>
  );
}
